// In useGridReportView.ts
import xeniaApi from 'api/index';
import { useEffect, useState, useCallback } from 'react';
import {
  GridReportView as GridReportViewType,
  GridReportViewThreshold,
  GridReportViewThresholdComparator,
} from '../models';
import useAI from 'utils/CustomHooks/useAI';
import { omit } from 'lodash';

function useGridReportView(gridReportViewId: string) {
  const [gridReportView, setGridReportView] =
    useState<GridReportViewType | null>(null);
  const [projectFrequency, setProjectFrequency] = useState<any>(null);
  const [gridReportViewData, setGridReportViewData] = useState<any>(null);
  const [gridReportThresholds, setGridReportThresholds] = useState<
    GridReportViewThreshold[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const {
    canUseAI,
    insights,
    isGenerating,
    error: aiError,
    generateAIInsights,
    regenerateAIInsights,
    contextId,
  } = useAI();

  // Memoize the threshold fetching function
  const getGridReportViewThresholds = useCallback(async () => {
    try {
      const response = await xeniaApi.getGridReportViewThresholds(
        gridReportViewId,
      );
      setGridReportThresholds(response as GridReportViewThreshold[]);
      return response;
    } catch (error) {
      console.error('Failed to fetch thresholds:', error);
      return [];
    }
  }, [gridReportViewId]);

  // Function to generate insights from report data
  const generateInsightsFromData = useCallback(
    async (reportData: any, viewData: any) => {
      if (!canUseAI || !reportData || !viewData) {
        return;
      }

      // Format grid report data for AI context
      const contextData = {
        reportId: gridReportViewId,
        reportData: viewData.map((item: any) =>
          omit(item, [
            'isLocationRow',
            'isLocationAggregate',
            'location',
            'project',
          ]),
        ),
      };

      await generateAIInsights('grid-report', contextData);
    },
    [canUseAI, generateAIInsights, gridReportViewId],
  );

  useEffect(() => {
    setIsLoading(true);
    xeniaApi
      .getGridReportViewById(gridReportViewId)
      .then(async (data) => {
        setProjectFrequency(data.projectFrequency);
        setGridReportView(data.gridReportView as GridReportViewType);
        setGridReportViewData(data.dataset);

        // Generate AI insights from the data
        await generateInsightsFromData(data.gridReportView, data.dataset);
      })
      .then(async () => {
        await getGridReportViewThresholds();
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [gridReportViewId, getGridReportViewThresholds, generateInsightsFromData]);

  async function updateGridReportView(data: GridReportViewType) {
    try {
      const response = await xeniaApi.updateGridReportView(
        gridReportViewId,
        data,
      );
      setGridReportView(response.gridReportView as GridReportViewType);
      setGridReportViewData(response.dataset);

      // Generate new insights when data changes
      await generateInsightsFromData(response.gridReportView, response.dataset);
    } catch (error) {
      console.error('Failed to update grid report view:', error);
    }
  }

  async function createGridReportViewThreshold(thresholdData: {
    color: string;
    label: string;
    comparator: GridReportViewThresholdComparator;
    value: number;
    secondValue?: number;
  }) {
    try {
      const response = await xeniaApi.createGridReportViewThreshold(
        gridReportViewId,
        thresholdData,
      );
      if (response) {
        // Immediately refresh thresholds after creation
        await getGridReportViewThresholds();
        return response;
      }
    } catch (error) {
      console.error('Failed to create threshold:', error);
      throw error;
    }
  }

  async function updateGridReportViewThreshold(
    thresholdId: string,
    thresholdData: {
      color?: string;
      label?: string;
      comparator?: GridReportViewThresholdComparator;
      value?: number;
      secondValue?: number;
    },
  ) {
    try {
      const response = await xeniaApi.updateGridReportViewThreshold(
        gridReportViewId,
        thresholdId,
        thresholdData,
      );
      if (response) {
        // Immediately refresh thresholds after update
        await getGridReportViewThresholds();
        return response;
      }
    } catch (error) {
      console.error('Failed to update threshold:', error);
      throw error;
    }
  }

  async function deleteGridReportViewThreshold(thresholdId: string) {
    try {
      const response = await xeniaApi.deleteGridReportViewThreshold(
        gridReportViewId,
        thresholdId,
      );
      if (response) {
        // Immediately refresh thresholds after deletion
        await getGridReportViewThresholds();
      }
    } catch (error) {
      console.error('Failed to delete threshold:', error);
      throw error;
    }
  }

  return {
    projectFrequency,
    gridReportView,
    isLoading,
    gridReportViewData,
    gridReportThresholds,
    updateGridReportView,
    createGridReportViewThreshold,
    updateGridReportViewThreshold,
    deleteGridReportViewThreshold,
    getGridReportViewThresholds,
    // AI Insights
    aiInsights: insights,
    aiError,
    isGeneratingInsights: isGenerating,
    regenerateInsights: regenerateAIInsights,
    canUseAI,
    contextId,
  };
}

export default useGridReportView;
